import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './button';
import { Badge } from './badge';
import {
  Paperclip,
  X,
  File,
  Image,
  FileText,
  Archive,
} from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { sanitizeFilenameForAzure, validateAzureFilename, createFileWithSanitizedName } from '@/utils/fileUtils';

interface AttachmentFile extends File {
  id: string;
  preview?: string;
}

interface CompactFileUploadProps {
  onFilesChange: (files: AttachmentFile[]) => void;
  maxFiles?: number;
  maxSize?: number; // in bytes
  acceptedFileTypes?: {
    [key: string]: string[];
  };
  className?: string;
  resetTrigger?: number; // Add reset trigger prop
}

const defaultAcceptedTypes = {
  'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/zip': ['.zip'],
  'application/x-rar-compressed': ['.rar'],
  'text/plain': ['.txt'],
};

export const CompactFileUpload: React.FC<CompactFileUploadProps> = ({
  onFilesChange,
  maxFiles = 3,
  maxSize = 5 * 1024 * 1024, // 5MB
  acceptedFileTypes = defaultAcceptedTypes,
  className,
  resetTrigger = 0,
}) => {
  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const filesRef = useRef<AttachmentFile[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: Array<{ file: File; errors: Array<{ code: string; message: string }> }>) => {
      setErrors([]);
      const newErrors: string[] = [];

      // Handle rejected files
      rejectedFiles.forEach(({ file, errors }) => {
        errors.forEach((error: { code: string; message: string }) => {
          if (error.code === 'file-too-large') {
            newErrors.push(`${file.name} is too large (max ${formatFileSize(maxSize)})`);
          } else if (error.code === 'file-invalid-type') {
            newErrors.push(`${file.name} type not supported`);
          } else if (error.code === 'too-many-files') {
            newErrors.push(`Too many files (max ${maxFiles})`);
          } else {
            newErrors.push(`${file.name}: ${error.message}`);
          }
        });
      });

      // Check if we can accept more files
      if (files.length + acceptedFiles.length > maxFiles) {
        newErrors.push(`Cannot add more than ${maxFiles} files`);
        const remainingSlots = maxFiles - files.length;
        acceptedFiles = acceptedFiles.slice(0, remainingSlots);
      }

      // Process accepted files with filename sanitization
      const processedFiles: AttachmentFile[] = acceptedFiles.map((file) => {
        // Validate and sanitize filename
        const validation = validateAzureFilename(file.name);
        const sanitizedFilename = sanitizeFilenameForAzure(file.name);
        
        // If filename had issues, add to errors array
        if (!validation.isValid) {
          newErrors.push(`File "${file.name}" renamed to "${sanitizedFilename}" for Azure compatibility`);
        }
        
        // Create attachment file with sanitized name
        const sanitizedFile = createFileWithSanitizedName(file, sanitizedFilename);
        const attachmentFile = Object.assign(sanitizedFile, {
          id: Math.random().toString(36).substring(2, 15)
        }) as AttachmentFile;
        
        // Create preview for images
        if (file.type.startsWith('image/')) {
          attachmentFile.preview = URL.createObjectURL(file);
        }
        
        return attachmentFile;
      });

      const updatedFiles = [...files, ...processedFiles];
      setFiles(updatedFiles);
      onFilesChange(updatedFiles);
      
      if (newErrors.length > 0) {
        setErrors(newErrors);
      }
    },
    [files, maxFiles, maxSize, onFilesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize,
    maxFiles: maxFiles - files.length,
    noClick: files.length >= maxFiles,
  });

  const removeFile = (fileId: string) => {
    const updatedFiles = files.filter((file) => {
      if (file.id === fileId && file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return file.id !== fileId;
    });
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="h-3 w-3" />;
    if (fileType === 'application/pdf') return <FileText className="h-3 w-3" />;
    if (fileType.includes('zip') || fileType.includes('rar')) return <Archive className="h-3 w-3" />;
    return <File className="h-3 w-3" />;
  };

  // Reset files when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      // Cleanup existing previews using ref to avoid dependency issues
      filesRef.current.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      setFiles([]);
      setErrors([]);
      onFilesChange([]);
    }
  }, [resetTrigger, onFilesChange]);

  // Cleanup function to revoke object URLs for previews on unmount
  useEffect(() => {
    const currentFiles = filesRef.current;
    return () => {
      currentFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      {/* Attach Button with Hover Card */}
      <HoverCard>
        <HoverCardTrigger asChild>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'h-6 w-6 p-0',
                isDragActive && 'bg-primary/10',
                files.length >= maxFiles && 'opacity-50 cursor-not-allowed'
              )}
              disabled={files.length >= maxFiles}
            >
              <Paperclip className="h-3 w-3" />
            </Button>
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-64 p-3" side="top">
          <div className="space-y-2">
            <p className="text-xs font-medium">Attach files</p>
            <p className="text-xs text-muted-foreground">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-muted-foreground">
              Max {maxFiles} files, {formatFileSize(maxSize)} each
            </p>
            {errors.length > 0 && (
              <div className="space-y-1">
                {errors.map((error, index) => (
                  <p key={index} className="text-xs text-destructive">
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>

      {/* File List - Compact */}
      {files.length > 0 && (
        <div className="flex items-center gap-1">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md"
            >
              <div className="flex items-center gap-1">
                {getFileIcon(file.type)}
                <span className="text-xs truncate max-w-20" title={file.name}>
                  {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(file.id)}
                className="h-4 w-4 p-0 hover:bg-destructive/10"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          {files.length > 0 && (
            <Badge variant="secondary" className="text-xs h-5">
              {files.length}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default CompactFileUpload;
